import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Paper,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Alert,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  fetchAuditList,
  // invalidateAuditCache,
} from "../../../api/FetchAuditList";
import { updateAuditData } from "../../../utils/updateAuditData";
import KanbanColumn from "./KanbanColumn";

export default function AuditList({ refreshTrigger, onAuditClick }) {
  const [audits, setAudits] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAudit, setMenuAudit] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define status column configuration - moved outside to prevent re-creating on each render
  const statuses = useMemo(
    () => [
      { id: "Planned", name: "Planned", color: "#FFD700", bgColor: "#FFFACD" },
      {
        id: "Scheduled",
        name: "Scheduled",
        color: "#1E90FF",
        bgColor: "#E6F7FF",
      },
      {
        id: "In-process",
        name: "In-process",
        color: "#FFA500",
        bgColor: "#FFF5E6",
      },
      {
        id: "Completed",
        name: "Completed",
        color: "#32CD32",
        bgColor: "#E6FFE6",
      },
      {
        id: "Cancelled",
        name: "Cancelled",
        color: "#FF4500",
        bgColor: "#FFE6E6",
      },
    ],
    []
  );

  const fetchAudits = async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      setError(null);
      const auditsData = await fetchAuditList(forceRefresh);
      setAudits(auditsData);
    } catch (error) {
      console.error("Failed to fetch audits:", error);
      setError("Failed to load audit data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch - runs only once on component mount
  useEffect(() => {
    fetchAudits();

    // Set up a refresh interval (every 5 minutes)
    const intervalId = setInterval(() => {
      fetchAudits(true); // Force refresh every 5 minutes
    }, 5 * 60 * 1000);

    // Clean up the interval
    return () => clearInterval(intervalId);
  }, []);

  // // Refresh when trigger changes
  // useEffect(() => {
  //   if (refreshTrigger) {
  //     // invalidateAuditCache(); // Invalidate cache first
  //     fetchAudits(true);
  //     setSelectedStatus(null);
  //   }
  // }, [refreshTrigger]);

  // Refresh when trigger changes - make this more robust
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      console.log("Refresh trigger changed, fetching fresh data");
      fetchAudits(true); // Always force refresh when trigger changes
      setSelectedStatus(null);
    }
  }, [refreshTrigger]);

  // Optimization: Memoize the grouped audits calculation
  const groupedAudits = useMemo(() => {
    return audits.reduce((acc, audit) => {
      const status = audit.auditStatus || "Unknown";
      if (!acc[status]) acc[status] = [];
      acc[status].push(audit);
      return acc;
    }, {});
  }, [audits]);

  const handleCardClick = (audit, status) => {
    // Don't trigger click when we're dragging
    if (isDragging) return;

    setSelectedStatus(status); // Set the selected column
    onAuditClick(audit); // Trigger inline view
  };

  const handleMenuOpen = (event, audit) => {
    event.stopPropagation(); // Prevent card click
    setMenuAnchor(event.currentTarget); // Set the anchor for the menu
    setMenuAudit(audit); // Track the audit for which the menu is open
  };

  const handleMenuClose = () => {
    setMenuAnchor(null); // Close the menu
    setMenuAudit(null); // Clear the tracked audit
  };

  const handleMenuAction = async (action) => {
    if (!menuAudit) return;

    if (action === "Cancelled") {
      try {
        // Optimistic update for UI
        const updatedAudits = audits.map((audit) =>
          audit.id === menuAudit.id
            ? { ...audit, auditStatus: "Cancelled" }
            : audit
        );
        setAudits(updatedAudits);
        // invalidateAuditCache(); // Invalidate cache when we update data

        // Send update to API
        await updateAuditData(menuAudit.id, { Status: "Cancelled" });
      } catch (error) {
        console.error("Failed to update audit status:", error);
        // Revert to original state on error
        fetchAudits(true);
      }
    } else if (action === "Delete") {
      // Handle delete action if needed
      console.log(`Delete action for Audit ID: ${menuAudit?.id}`);
    }

    handleMenuClose(); // Close the menu after action
  };

  // Handle drag end event
  const onDragEnd = async (result) => {
    setIsDragging(false);

    const { source, destination, draggableId } = result;

    // If dropped outside a droppable area or in same position
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    ) {
      return;
    }

    // Find the audit being dragged
    const auditId = draggableId;
    const draggedAudit = audits.find((audit) => audit.id === auditId);

    if (!draggedAudit) {
      console.error("Could not find audit with ID:", auditId);
      return;
    }

    // Get the new status from destination droppable ID
    const newStatus = destination.droppableId;

    // Only update if status has changed
    if (draggedAudit.auditStatus !== newStatus) {
      try {
        // Optimistic update for UI
        const updatedAudits = audits.map((audit) =>
          audit.id === auditId ? { ...audit, auditStatus: newStatus } : audit
        );
        setAudits(updatedAudits);
        // invalidateAuditCache(); // Invalidate cache when we update data

        // Send update to API
        await updateAuditData(auditId, { Status: newStatus });
      } catch (error) {
        console.error("Failed to update audit status:", error);
        // Revert to original state on error
        fetchAudits(true);
      }
    }
  };

  // Track drag start to prevent card click during drag
  const onDragStart = () => {
    setIsDragging(true);
  };

  // Loading skeleton for columns
  if (isLoading && audits.length === 0) {
    return (
      <Box sx={{ display: "flex", gap: 2 }}>
        {[1, 2, 3, 4, 5].map((index) => (
          <Box
            key={`skeleton-${index}`}
            sx={{
              flex: "0 0 300px",
              minHeight: "800px",
              bgcolor: "#fff",
              p: 1,
            }}
          >
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            {[1, 2, 3, 4].map((cardIndex) => (
              <Skeleton
                key={`card-${index}-${cardIndex}`}
                variant="rectangular"
                height={100}
                sx={{ mb: 2 }}
              />
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={
          <IconButton
            color="inherit"
            size="small"
            onClick={() => fetchAudits(true)}
          >
            Retry
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
      <Box sx={{ display: "flex", gap: 2 }}>
        {statuses.map((status) => (
          <KanbanColumn
            key={status.id}
            status={status}
            selectedStatus={selectedStatus}
            groupedAudits={groupedAudits}
            isLoading={isLoading}
            handleCardClick={handleCardClick}
            handleMenuOpen={handleMenuOpen}
          />
        ))}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleMenuAction("Cancelled")}>
            Cancelled
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction("Delete")}>Delete</MenuItem>
        </Menu>
      </Box>
    </DragDropContext>
  );
}
